import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, Clock, Loader2, Plus, Trash2, Lock, Ban } from "lucide-react";
import TimePicker from "./TimePicker";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { API } from "@/App";
import { format, addDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function TrainerTimeBlockModal({
  isOpen,
  onClose,
  trainer,
  businessId,
  canEdit = true, // Can the current user edit/delete blocks
}) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteBlockId, setDeleteBlockId] = useState(null); // For delete confirmation
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    start_time: "09:00",
    end_time: "18:00",
    reason: "",
    all_day: false,
  });

  useEffect(() => {
    if (isOpen && trainer) {
      fetchBlocks();
    }
  }, [isOpen, trainer]);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const startDate = format(new Date(), "yyyy-MM-dd");
      const endDate = format(addDays(new Date(), 60), "yyyy-MM-dd");
      
      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/time-blocks?business_id=${businessId}&start_date=${startDate}&end_date=${endDate}`,
        { credentials: "include" }
      );
      if (response.ok) {
        setBlocks(await response.json());
      }
    } catch (error) {
      console.error("Error fetching blocks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async () => {
    if (!formData.date) {
      toast.error(t("common:toast_tarihSecin"));
      return;
    }
    if (!formData.all_day && (!formData.start_time || !formData.end_time)) {
      toast.error(t("common:toast_saatSecinVeyaTumGun"));
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/time-blocks`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            business_id: businessId,
            date: formData.date,
            start_time: formData.all_day ? "00:00" : formData.start_time,
            end_time: formData.all_day ? "23:59" : formData.end_time,
            reason: formData.reason,
            all_day: formData.all_day,
          }),
        }
      );

      if (response.ok) {
        toast.success(t('reservations:timeBlockAdded'));
        setShowAddForm(false);
        setFormData({
          date: format(new Date(), "yyyy-MM-dd"),
          start_time: "09:00",
          end_time: "18:00",
          reason: "",
          all_day: false,
        });
        fetchBlocks();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/time-blocks/${blockId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(t('reservations:timeBlockDeleted'));
        setDeleteBlockId(null);
        fetchBlocks();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Generate date options (next 60 days)
  const dateOptions = [];
  for (let i = 0; i < 60; i++) {
    const date = addDays(new Date(), i);
    dateOptions.push({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "d MMMM yyyy, EEEE", { locale: getDateLocale() }),
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-600" />
            {t("common:timeBlockTitle")} - {trainer?.name} {trainer?.surname}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <p className="text-sm text-grey">
            {t("common:timeBlockDesc")}
            
          </p>

          {/* Add Button */}
          {canEdit && !showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("common:addNewTimeBlock")}
            </Button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl space-y-4">
              <h4 className="font-medium text-orange-800">{t("common:newTimeBlock")}</h4>

              <div>
                <Label>{t("common:dateRequired")}</Label>
                <DatePickerTR
                  value={formData.date ? new Date(formData.date) : null}
                  onChange={(date) => setFormData({ ...formData, date: date ? toLocalDateString(date) : "" })}
                  placeholder={t('common:selectDate')}
                  minDate={new Date()}
                />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.all_day}
                  onCheckedChange={(checked) => setFormData({ ...formData, all_day: checked })}
                />
                <Label>{t("common:allDay")}</Label>
              </div>

              {!formData.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('settings:startTime')}</Label>
                    <TimePicker
                      value={formData.start_time}
                      onChange={(val) => setFormData({ ...formData, start_time: val })}
                      minHour={6}
                      maxHour={23}
                      minuteStep={15}
                    />
                  </div>
                  <div>
                    <Label>{t('settings:endTime')}</Label>
                    <TimePicker
                      value={formData.end_time}
                      onChange={(val) => setFormData({ ...formData, end_time: val })}
                      minHour={6}
                      maxHour={23}
                      minuteStep={15}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>{t("common:reasonOptional")}</Label>
                <Textarea
                  placeholder={t("common:ph_i̇zinDoktorRandevusuVb")}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="rounded-xl"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setShowAddForm(false)}
                >
                  {t("common:giveUp2")}
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl"
                  onClick={handleAddBlock}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('common:add')
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Existing Blocks */}
          <div className="space-y-2">
            <h4 className="font-medium text-navy">{t("common:existingTimeBlocks")}</h4>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-navy" />
              </div>
            ) : blocks.length === 0 ? (
              <div className="text-center py-8 text-grey">
                <Ban className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("common:noTimeBlocksYet")}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {blocks.map((block) => (
                  <div
                    key={block.block_id}
                    className="flex items-center justify-between p-3 bg-slate-50 border rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-navy">
                          {format(new Date(block.date), "d MMMM yyyy, EEEE", { locale: getDateLocale() })}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-grey">
                          <Clock className="w-3 h-3" />
                          {block.all_day ? (
                            <Badge variant="secondary">{t("common:allDay")}</Badge>
                          ) : (
                            <span>{block.start_time} - {block.end_time}</span>
                          )}
                          {block.reason && (
                            <span className="text-xs">• {block.reason}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteBlockId(block.block_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delete Confirmation Dialog */}
          {deleteBlockId && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
              <div className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                <span className="font-medium">{t("admin:deleteConfirmation")}</span>
              </div>
              <p className="text-sm text-red-600">
                {t("common:deleteTimeBlockConfirm")}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setDeleteBlockId(null)}
                >
                  {t("common:giveUp2")}
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl"
                  onClick={() => handleDeleteBlock(deleteBlockId)}
                >
                  {t("common:delete")}
                </Button>
              </div>
            </div>
          )}

          <Button
            variant="outline"
            className="w-full rounded-xl"
            onClick={onClose}
          >
            {t("common:close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
